import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import type { IOpenstackLabComponent, LabDriverComponentProps } from '../types';
import VMRow from './VMRow';

const OpenstackLabComponent: React.FC<LabDriverComponentProps> = observer(
  ({ action, labComponent, labStore }) => {
    const { t } = useTranslation();
    const openstackComponent = labComponent as IOpenstackLabComponent;
    const { context } = openstackComponent;

    const doCommand = useCallback(
      async (command: string, params: { vm_id: string }) => {
        if (!action || !labStore) {
          return null;
        }

        let result = null;

        if (command !== 'vm_reset') {
          result = await action(command, params);
        } else {
          labStore.createNewModal(
            {
              confirmFunc: async () => {
                labStore.setModalWorking(true);
                try {
                  result = await action(command, params);
                } finally {
                  labStore.setModalWorking(false);
                  labStore.hideModal();
                }
              },
              confirmText: t('Yes, Reset it'),
              dismissText: t('No'),
              title: t('Are you sure you want to reset this VM?'),
            },
            true,
          );
        }

        if (command === 'get_vnc_url' && result?.data?.results) {
          result.data.results.forEach(
            (res: {
              component?: { driver?: string };
              result?: { url?: string };
            }) => {
              if (!res.component) return;
              if (res.component.driver !== openstackComponent.driver) return;

              const vncUrl = res?.result?.url;
              if (vncUrl) {
                window.open(vncUrl);
              }
            },
          );
        }

        return result;
      },
      [action, openstackComponent.driver, labStore, t],
    );

    if (!action || !labStore) {
      return null;
    }

    const vmList = context?.stat?.vm_list || [];
    const vmAnnotations = context?.vm_annotations || [];

    return (
      <div className="lab-controls-vm-panel lab-controls-vm-panel--card">
        <div className="lab-card-vm-list">
          {vmList.map((vm) => (
            <VMRow
              action={doCommand}
              key={vm.id}
              labState={labStore.labState}
              vmAnnotations={vmAnnotations}
              vmSummary={vm}
            />
          ))}
        </div>
      </div>
    );
  },
);

export default OpenstackLabComponent;
